import React, { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, Images } from "lucide-react";

export default function GalleryLightbox({ images, emptyMessage }) {
  const [currentIndex, setCurrentIndex] = useState(null);
  const displayImages = images || [];

  if (displayImages.length === 0) {
    return (
      <div className="border border-stone-200 bg-white p-12 text-center text-stone-500 rounded-none shadow-sm space-y-3">
        <div className="mx-auto flex h-12 w-12 items-center justify-center border border-stone-200 bg-stone-50 text-stone-400">
          <Images className="h-6 w-6" />
        </div>
        <p className="text-sm font-light text-stone-600 tracking-wide-custom">
          {emptyMessage || "Ingen billeder i dette album endnu."}
        </p>
      </div>
    );
  }

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (currentIndex === null) return;
      if (e.key === "Escape") setCurrentIndex(null);
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  // Simple touch swipe support
  let touchStartX = 0;
  let touchEndX = 0;

  const handleTouchStart = (e) => {
    touchStartX = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX = e.changedTouches[0].screenX;
    if (touchStartX - touchEndX > 50) {
      handleNext();
    } else if (touchEndX - touchStartX > 50) {
      handlePrev();
    }
  };

  return (
    <div>
      {/* Grid of Images */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {displayImages.map((img, idx) => (
          <div
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className="group relative aspect-[4/3] overflow-hidden border border-stone-200 bg-stone-100 cursor-pointer rounded-none shadow-sm"
          >
            <img
              src={`${img}?width=600&quality=80&format=auto`}
              alt={`Equinesport Galleri ${idx + 1}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              loading="lazy"
            />
            {/* Hover Overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-stone-900/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <div className="flex h-12 w-12 items-center justify-center border border-stone-200 bg-white text-stone-800 rounded-none shadow-md">
                <ZoomIn className="h-5 w-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {currentIndex !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm select-none"
          onClick={() => setCurrentIndex(null)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close Button */}
          <button
            onClick={() => setCurrentIndex(null)}
            className="absolute right-6 top-6 z-50 border border-stone-800 bg-stone-900/80 p-3 text-stone-400 hover:border-stone-400 hover:text-white transition-colors duration-300 rounded-none cursor-pointer"
            aria-label="Luk visning"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Prev Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            className="absolute left-6 border border-stone-800 bg-stone-900/80 p-3 text-stone-400 hover:border-stone-400 hover:text-white transition-colors duration-300 md:flex hidden rounded-none cursor-pointer"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Image Container */}
          <div
            className="relative max-h-[85vh] max-w-[90vw] overflow-hidden w-[640px] h-[480px] max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${displayImages[currentIndex]}?width=1200&quality=85&format=auto`}
              alt={`Equinesport Galleri ${currentIndex + 1}`}
              className="w-full h-full object-contain"
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-stone-900/80 px-4 py-1.5 text-[10px] tracking-widest text-stone-400 uppercase rounded-none border border-stone-800">
              {currentIndex + 1} / {displayImages.length}
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="absolute right-6 border border-stone-800 bg-stone-900/80 p-3 text-stone-400 hover:border-stone-400 hover:text-white transition-colors duration-300 md:flex hidden rounded-none cursor-pointer"
            aria-label="Næste billede"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  );
}
